import React, { useState } from 'react';
import dayjs from 'dayjs';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import StyledQrCode from 'Views/Components/_HumanWritten/StyleQrCode';
import { TextInput } from 'Views/Components/New/TextInput';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';
import {
	refundUserEntitlement,
	updateUserEntitlementExpiry,
	updateUserEntitlementUses,
} from 'Services/Api/_HumanWritten/AnytimeTicketService';
import alertToast from 'Util/ToastifyUtils';
import { UserEntitlementDto } from './UserEntitlementsUtils';
import { TransferEntitlementModal } from './TransferEntitlementModal';
import { anytimeTicketLabel } from 'Util/_HumanWritten/anytimeTicketTerm';

export interface UserEntitlementCardProps {
	entitlement: UserEntitlementDto;
	refresh: () => void;
}

export default function UserEntitlementCard({ entitlement, refresh }: UserEntitlementCardProps) {
	const [viewModalOpen, setViewModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isEditingUses, setIsEditingUses] = useState(false);
	const [isSavingUses, setIsSavingUses] = useState(false);
	const [editUses, setEditUses] = useState<string>(String(entitlement.remainingUses));
	const [refundConfirmOpen, setRefundConfirmOpen] = useState(false);
	const [isRefunding, setIsRefunding] = useState(false);
	const [transferModalOpen, setTransferModalOpen] = useState(false);
	const [editExpiryDate, setEditExpiryDate] = useState<string | undefined>(
		entitlement.expiryDate ? dayjs(entitlement.expiryDate).format('YYYY-MM-DD') : undefined,
	);
	const isExpired = entitlement.expiryDate && new Date(entitlement.expiryDate) < new Date();
	const isDepleted = entitlement.remainingUses <= 0;
	const isDisabled = !entitlement.isActive;
	const inactive = isExpired || isDepleted || isDisabled;

	const handleCloseModal = () => {
		setViewModalOpen(false);
		setIsEditing(false);
		setIsEditingUses(false);
	};

	let status: 'active' | 'redeemed' | 'expired' | 'voided' = 'active';
	if (isDisabled) status = 'voided';
	else if (isExpired) status = 'expired';
	else if (isDepleted) status = 'redeemed';

	const expiryText = entitlement.expiryDate
		? `${isExpired ? 'Expired' : 'Expires'} ${dayjs(entitlement.expiryDate).format('DD MMM YYYY')}`
		: 'No expiry';

	const productName = entitlement.entitlementProduct?.name || anytimeTicketLabel();
	// Build the QR from the rotatable QrCodeId (seeded equal to id) so a transferred pass shows
	// the valid, rotated code rather than the previous owner's now-invalid one.
	const qrUrl = `${window.location.origin}/qr-code/anytime-ticket/${entitlement.qrCodeId ?? entitlement.id}`;
	// Transfer is gated on RedeemEntitlement server-side.
	const canTransfer = appConfigStore.entitlementTransferEnabled && store.can('RedeemEntitlement') && !inactive;
	const canEditExpiry = store.can('ExtendEntitlementExpiry');
	const canEditUses = store.can('AdjustEntitlementUses');

	const handleConfirmRefund = async () => {
		setIsRefunding(true);
		try {
			const result = await refundUserEntitlement(entitlement.id);
			alertToast(`Refunded $${result.refundedAmount.toFixed(2)}.`, 'success');
			setRefundConfirmOpen(false);
			setViewModalOpen(false);
			refresh();
		} catch (e: any) {
			const message = e?.response?.data || 'Failed to process refund. Please try again.';
			alertToast(typeof message === 'string' ? message : 'Failed to process refund.', 'error');
		} finally {
			setIsRefunding(false);
		}
	};

	const handleSaveExpiry = async () => {
		setIsSaving(true);
		try {
			const dateToSave = editExpiryDate ? dayjs(editExpiryDate).endOf('day').toISOString() : null;

			await updateUserEntitlementExpiry(entitlement.id, dateToSave);
			refresh();
			handleCloseModal();
		} catch (e) {
			console.error('Failed to update expiry', e);
			alert('Failed to update expiry date. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	const handleSaveUses = async () => {
		const parsed = Number(editUses);
		if (!Number.isInteger(parsed) || parsed < 0) {
			alertToast('Enter a whole number of uses (0 or more).', 'error');
			return;
		}
		// Mirrors the backend cap: never more than the uses originally purchased.
		if (parsed > entitlement.initialUses) {
			alertToast(`Remaining uses can't exceed the original ${entitlement.initialUses}.`, 'error');
			return;
		}
		setIsSavingUses(true);
		try {
			await updateUserEntitlementUses(entitlement.id, parsed);
			alertToast('Updated remaining uses.', 'success');
			refresh();
			handleCloseModal();
		} catch (e: any) {
			const message = e?.response?.data || 'Failed to update remaining uses. Please try again.';
			alertToast(typeof message === 'string' ? message : 'Failed to update remaining uses.', 'error');
		} finally {
			setIsSavingUses(false);
		}
	};

	let modalLabel = `${anytimeTicketLabel()} QR Code`;
	if (isEditing) {
		modalLabel = 'Edit Expiry Date';
	} else if (isEditingUses) {
		modalLabel = 'Edit Remaining Uses';
	}

	return (
		<>
			<div
				className="voucher-ticket-wrapper"
				data-test-id="user-entitlement-card"
				onClick={() => setViewModalOpen(true)}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						setViewModalOpen(true);
					}
				}}
				role="button"
				tabIndex={0}
			>
				<div className={`voucher-ticket voucher-ticket--credit ${inactive ? 'voucher-ticket--inactive' : ''}`}>
					{/* Left stub */}
					<div className="voucher-ticket__stub">
						<span className="voucher-ticket__stub-label">{anytimeTicketLabel()}</span>
						<Icon name="confirmation_number" useMaterialIcons />
					</div>

					{/* Main body */}
					<div className="voucher-ticket__body">
						<div className="voucher-ticket__watermark">
							<Icon name="confirmation_number" useMaterialIcons />
						</div>

						<div className="voucher-ticket__content">
							<div className="voucher-ticket__top-row">
								<div>
									<p className="voucher-ticket__type-label">{anytimeTicketLabel()}</p>
									<div className="voucher-ticket__code-row">
										<span className="voucher-ticket__code">{productName}</span>
									</div>
								</div>
								<div className="voucher-ticket__amount">
									{entitlement.remainingUses}
									<span className="voucher-ticket__initial-amount"> Uses</span>
								</div>
							</div>

							<div className="voucher-ticket__bottom-row">
								<div className="voucher-ticket__meta">
									<span className={`voucher-ticket__status voucher-ticket__status--${status}`}>
										{status === 'redeemed' && <Icon name="check_circle" useMaterialIcons />}
										{status === 'expired' && <Icon name="error" useMaterialIcons />}
										{status === 'voided' && <Icon name="block" useMaterialIcons />}
										{
											{
												active: 'Active',
												redeemed: 'Depleted',
												expired: 'Expired',
												voided: 'Voided',
											}[status]
										}
									</span>
									<span className="voucher-ticket__expiry">
										<Icon name="schedule" useMaterialIcons />
										{expiryText}
									</span>
									<span className="voucher-ticket__expiry"># {entitlement.humanReadableId}</span>
									{entitlement.appliedPriceOverrideName && (
										<span
											className="voucher-ticket__expiry"
											title={`${entitlement.appliedPriceOverrideName} rate applied`}
										>
											<Icon name="sell" useMaterialIcons />
											{entitlement.appliedPriceOverrideName} rate
										</span>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<NewModal isOpen={viewModalOpen} onRequestClose={handleCloseModal} label={modalLabel} modalSize="sm">
				<NewModal.Body>
					<div className="flex flex-col items-center justify-center gap-4 text-center">
						{isEditing && (
							<div className="w-full text-left">
								<h3 className="mb-4 text-lg font-bold text-gray-900">{productName}</h3>
								<div className="mt-4">
									<TextInput
										id="expiry-edit"
										label="Override Expiry Date"
										type="date"
										value={editExpiryDate || ''}
										onChange={e => setEditExpiryDate(e.target.value)}
										disabled={isSaving}
									/>
								</div>
							</div>
						)}
						{isEditingUses && (
							<div className="w-full text-left">
								<h3 className="mb-4 text-lg font-bold text-gray-900">{productName}</h3>
								<div className="mt-4">
									<TextInput
										id="uses-edit"
										label="Remaining uses"
										type="number"
										value={editUses}
										onChange={e => setEditUses(e.target.value)}
										disabled={isSavingUses}
									/>
									<p className="mt-1 text-sm text-gray-500">
										Max {entitlement.initialUses} (the original uses purchased).
									</p>
								</div>
							</div>
						)}
						{!isEditing && !isEditingUses && (
							<>
								<StyledQrCode value={qrUrl} />
								<p className="mt-6 text-xl font-bold text-gray-900">{productName}</p>
								<p className="mt-1 text-gray-500">Remaining: {entitlement.remainingUses} Uses</p>
								{entitlement.appliedPriceOverrideName && (
									<p className="mt-1 text-sm text-gray-500">
										{entitlement.appliedPriceOverrideName} rate applied
									</p>
								)}
							</>
						)}
					</div>
				</NewModal.Body>
				<NewModal.Footer>
					<div className="flex flex-col w-full gap-2">
						{isEditing && (
							<>
								<Button
									display={Display.Solid}
									colors={Colors.Primary}
									newStyles
									onClick={handleSaveExpiry}
									disabled={isSaving}
								>
									{isSaving ? 'Saving...' : 'Save Changes'}
								</Button>
								<Button
									display={Display.Outline}
									colors={Colors.Black}
									newStyles
									onClick={() => setIsEditing(false)}
									disabled={isSaving}
								>
									Cancel
								</Button>
							</>
						)}
						{isEditingUses && (
							<>
								<Button
									display={Display.Solid}
									colors={Colors.Primary}
									newStyles
									onClick={handleSaveUses}
									disabled={isSavingUses}
								>
									{isSavingUses ? 'Saving...' : 'Save Changes'}
								</Button>
								<Button
									display={Display.Outline}
									colors={Colors.Black}
									newStyles
									onClick={() => setIsEditingUses(false)}
									disabled={isSavingUses}
								>
									Cancel
								</Button>
							</>
						)}
						{!isEditing && !isEditingUses && (
							<>
								{(canEditExpiry || canEditUses) && (
									<div className="flex w-full gap-2">
										{canEditExpiry && (
											<Button
												display={Display.Outline}
												colors={Colors.Primary}
												newStyles
												onClick={() => setIsEditing(true)}
												className="flex-1"
											>
												Edit expiry
											</Button>
										)}
										{canEditUses && (
											<Button
												display={Display.Outline}
												colors={Colors.Primary}
												newStyles
												onClick={() => {
													setEditUses(String(entitlement.remainingUses));
													setIsEditingUses(true);
												}}
												className="flex-1"
											>
												Edit uses
											</Button>
										)}
									</div>
								)}
								{entitlement.allowsRefund && !inactive && (
									<Button
										display={Display.Text}
										colors={Colors.Error}
										newStyles
										onClick={() => setRefundConfirmOpen(true)}
										className="w-full"
									>
										Refund
									</Button>
								)}
								{canTransfer && (
									<Button
										display={Display.Text}
										colors={Colors.Primary}
										newStyles
										onClick={() => setTransferModalOpen(true)}
										className="w-full"
										buttonProps={{ 'data-test-id': 'entitlement-transfer-button' } as any}
									>
										Transfer to another user
									</Button>
								)}
								<Button
									display={Display.Text}
									colors={Colors.Black}
									newStyles
									onClick={handleCloseModal}
									className="w-full"
								>
									Close
								</Button>
							</>
						)}
					</div>
				</NewModal.Footer>
			</NewModal>

			<NewModal
				isOpen={refundConfirmOpen}
				onRequestClose={() => !isRefunding && setRefundConfirmOpen(false)}
				label="Confirm refund"
				modalSize="sm"
			>
				<NewModal.Body>
					<div className="flex flex-col gap-3 text-center">
						<p className="text-base text-gray-900">
							Refund <strong>${entitlement.refundableAmount.toFixed(2)}</strong> to the original payment
							method?
						</p>
						<p className="text-sm text-gray-500">
							{productName} — {entitlement.remainingUses} use{entitlement.remainingUses === 1 ? '' : 's'}{' '}
							remaining. This ticket will be deactivated.
						</p>
					</div>
				</NewModal.Body>
				<NewModal.Footer>
					<div className="flex flex-col w-full gap-2">
						<Button
							display={Display.Solid}
							colors={Colors.Error}
							newStyles
							onClick={handleConfirmRefund}
							disabled={isRefunding}
							className="w-full"
						>
							{isRefunding ? 'Processing...' : 'Confirm refund'}
						</Button>
						<Button
							display={Display.Outline}
							colors={Colors.Black}
							newStyles
							onClick={() => setRefundConfirmOpen(false)}
							disabled={isRefunding}
							className="w-full"
						>
							Cancel
						</Button>
					</div>
				</NewModal.Footer>
			</NewModal>

			<TransferEntitlementModal
				entitlement={entitlement}
				isOpen={transferModalOpen}
				onClose={() => setTransferModalOpen(false)}
				onTransferred={() => {
					setTransferModalOpen(false);
					setViewModalOpen(false);
					refresh();
				}}
			/>
		</>
	);
}
